import type { ReactNode } from 'react';
import type { Notification } from '../../generated/graphql';

/**
 * The tile a notification row opens with.
 *
 * The SP app's panel leads every row with a 32px rounded tile carrying a glyph
 * tinted by what the notification is about — green for anything financial,
 * amber for a warning, red for a failure — and the client portal led with
 * nothing at all, so its rows read as a wall of text with no way to tell an
 * expired estimate from a new login at a glance.
 *
 * The glyphs are written out here rather than pulled from an icon package,
 * which this project does not use, and rather than imported from
 * `src/assets/icons` as the rest of the app does: those are loaded as URLs and
 * drawn in an `<img>`, so their colour is baked in and a tone class could not
 * reach them. Inline they inherit `currentColor` and the tone works.
 *
 * The selection follows the SP app's, case for case, so the two panels sort
 * the same notification into the same colour.
 */

const glyph = (children: ReactNode) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const CircleCheck = glyph(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 12l2 2 4-4" />
  </>
);

const CircleX = glyph(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 10l4 4m0-4l-4 4" />
  </>
);

const CalendarEvent = glyph(
  <>
    <rect x="4" y="5" width="16" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M4 11h16M8 15h2v2H8z" />
  </>
);

const User = glyph(
  <>
    <circle cx="12" cy="7" r="4" />
    <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
  </>
);

const Package = glyph(
  <>
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
    <path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" />
  </>
);

const MessageCircle = glyph(
  <>
    <path d="M12 20a8 8 0 10-6.9-3.9L4 20l3.9-1.1A8 8 0 0012 20z" />
    <path d="M9 10h6M9 14h3" />
  </>
);

const FileText = glyph(
  <>
    <path d="M14 3v4a1 1 0 001 1h4" />
    <path d="M17 21H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
    <path d="M9 13h6M9 17h6" />
  </>
);

const AlertTriangle = glyph(
  <>
    <path d="M10.24 3.96L2.51 17.3A2 2 0 004.24 20h15.52a2 2 0 001.73-2.7L13.76 3.96a2 2 0 00-3.52 0z" />
    <path d="M12 9v4M12 17h.01" />
  </>
);

const Bell = glyph(
  <>
    <path d="M10 5a2 2 0 114 0 7 7 0 014 6v3a4 4 0 002 3H4a4 4 0 002-3v-3a7 7 0 014-6" />
    <path d="M9 17v1a3 3 0 006 0v-1" />
  </>
);

export interface NotificationTone {
  icon: ReactNode;
  toneClass: string;
}

export const getNotificationIcon = (notification: Notification): NotificationTone => {
  const type = (notification.type || '').toUpperCase();
  const category = (notification.category || '').toUpperCase();
  const title = (notification.title || '').toLowerCase();

  if (
    type.includes('SUCCESS') ||
    type.includes('COMPLETED') ||
    title.includes('successful') ||
    title.includes('success')
  ) {
    return { icon: CircleCheck, toneClass: 'bg-greenContainer text-onGreenContainer' };
  }

  if (
    type.includes('FAILED') ||
    type.includes('ERROR') ||
    title.includes('failed') ||
    title.includes('error')
  ) {
    return { icon: CircleX, toneClass: 'bg-redContainer text-onRedContainer' };
  }

  if (category.includes('APPOINTMENT') || type.includes('APPOINTMENT')) {
    return { icon: CalendarEvent, toneClass: 'bg-blueContainer text-onBlueContainer' };
  }

  if (category.includes('TEAM') || type.includes('USER')) {
    return { icon: User, toneClass: 'bg-blueContainer text-onBlueContainer' };
  }

  if (category.includes('INVENTORY') || type.includes('STOCK')) {
    return { icon: Package, toneClass: 'bg-yellowContainer text-onYellowContainer' };
  }

  if (category.includes('MESSAGE') || type.includes('MESSAGE')) {
    return { icon: MessageCircle, toneClass: 'bg-blueContainer text-onBlueContainer' };
  }

  if (category.includes('ESTIMATE') || category.includes('FINANCIAL') || type.includes('INVOICE')) {
    return { icon: FileText, toneClass: 'bg-greenContainer text-onGreenContainer' };
  }

  if (notification.priority === 'HIGH' || notification.priority === 'URGENT') {
    return { icon: AlertTriangle, toneClass: 'bg-yellowContainer text-onYellowContainer' };
  }

  return { icon: Bell, toneClass: 'bg-grayContainer text-onGrayContainer' };
};

/** The same bell, at the size the empty state draws it. */
export const EmptyStateBell = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 5a2 2 0 114 0 7 7 0 014 6v3a4 4 0 002 3H4a4 4 0 002-3v-3a7 7 0 014-6" />
    <path d="M9 17v1a3 3 0 006 0v-1" />
  </svg>
);
